export type ActivityState = 'ouvert' | 'a-venir' | 'complet'

export interface Activity {
  id: string
  name: string
  audience: string
  place: string
  schedule: string
  price: string
  availability: string
  state: ActivityState
  opensOn?: string
  image: string
}

export interface Registration {
  id: string
  activity: string
  participant: string
  initials: string
  schedule: string
  place: string
  nextSession: string
}

export interface Facility {
  id: string
  name: string
  capacity: string
  availability: string
  price: string
  place: string
}

export interface Equipment {
  id: string
  name: string
  detail: string
  availability: string
  price: string
}

export const atnaChips = [
  'Find an activity',
  'My registrations',
  'Book a space',
  'Rent equipment',
]

/** Enfant inscrit par defaut dans le flux ATNA d'inscription. */
export const inscriptionChild = { name: 'Emma', age: '9 years old', initials: 'EM' }

export const inscriptionCategories = ['Swimming', 'Soccer', 'Skating', 'Visual arts', 'Music']

export interface ActivitySlot {
  id: string
  category: string
  name: string
  day: string
  time: string
  place: string
  audience: string
  price: string
  spots: string
  /** Creneau mis en avant par ATNA (aucun conflit a l'horaire). */
  recommended?: boolean
  note?: string
}

export const availableSlots: ActivitySlot[] = [
  {
    id: 'natation-mer',
    category: 'Swimming',
    name: 'Swim lessons — Level 3',
    day: 'Wednesday',
    time: '4:30-5:15 p.m.',
    place: 'Bonnyville Swimming Pool',
    audience: 'Ages 8-10',
    price: '$68 · 10 sessions',
    spots: '4 spots left',
    recommended: true,
    note: 'No conflict with Emma’s schedule',
  },
  {
    id: 'natation-sam',
    category: 'Swimming',
    name: 'Swim lessons — Level 3',
    day: 'Saturday',
    time: '10-10:45 a.m.',
    place: 'Bonnyville Swimming Pool',
    audience: 'Ages 8-10',
    price: '$68 · 10 sessions',
    spots: '1 spot left',
  },
  {
    id: 'natation-ven',
    category: 'Swimming',
    name: 'Swim lessons — Level 3',
    day: 'Friday',
    time: '6-6:45 p.m.',
    place: 'Bonnyville Swimming Pool',
    audience: 'Ages 8-11',
    price: '$68 · 10 sessions',
    spots: '6 spots left',
  },
  {
    id: 'soccer-mar',
    category: 'Soccer',
    name: 'Recreational soccer',
    day: 'Tuesday',
    time: '5:30-6:45 p.m.',
    place: 'Lakeshore Drive Park',
    audience: 'Ages 8-10',
    price: '$95 · season',
    spots: '8 spots left',
    recommended: true,
    note: '5 minutes from your address',
  },
  {
    id: 'soccer-sam',
    category: 'Soccer',
    name: 'Recreational soccer',
    day: 'Saturday',
    time: '9-10:15 a.m.',
    place: 'Centennial Centre fields',
    audience: 'Ages 8-10',
    price: '$95 · season',
    spots: '3 spots left',
  },
  {
    id: 'patin-jeu',
    category: 'Skating',
    name: 'Skating — Beginner',
    day: 'Thursday',
    time: '5-6 p.m.',
    place: 'Centennial Centre arena',
    audience: 'Ages 7-10',
    price: '$72 · 12 sessions',
    spots: '5 spots left',
    recommended: true,
    note: 'Equipment provided by the Town',
  },
  {
    id: 'arts-mer',
    category: 'Visual arts',
    name: 'Visual arts workshop',
    day: 'Wednesday',
    time: '6-7:15 p.m.',
    place: 'Bonnyville and District Centennial Centre',
    audience: 'Ages 8-12',
    price: '$58 · 8 workshops',
    spots: '7 spots left',
    recommended: true,
    note: 'Materials included',
  },
  {
    id: 'musique-lun',
    category: 'Music',
    name: 'Music discovery — guitar',
    day: 'Monday',
    time: '5:15-6 p.m.',
    place: 'Bonnyville Seniors Drop-In Centre',
    audience: 'Ages 8-12',
    price: '$110 · 10 classes',
    spots: '2 spots left',
    recommended: true,
    note: 'Instrument loaned for the session',
  },
]

export const activities: Activity[] = [
  {
    id: 'natation-junior',
    name: 'Swim lessons — Junior',
    audience: 'Ages 8-12',
    place: 'Bonnyville Swimming Pool',
    schedule: 'Saturday · 10 a.m.',
    price: '$85',
    availability: '12 spots available',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/09aab17e-70f7-466f-a4f5-58c6bbdb4e7c.jpg',
  },
  {
    id: 'pickleball',
    name: 'Drop-in pickleball',
    audience: 'Adults',
    place: 'Centennial Centre gymnasium',
    schedule: 'Tuesday and Thursday · 6:30 p.m.',
    price: '$40 / season',
    availability: 'Registration opening soon',
    state: 'a-venir',
    opensOn: 'September 15 · 9 a.m.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/69ca7f97-304e-4e63-968b-9130fae01a37.jpg',
  },
  {
    id: 'fitness-50',
    name: 'Fitness 50 and over',
    audience: '50 and over',
    place: 'Bonnyville Seniors Drop-In Centre',
    schedule: 'Monday and Wednesday · 9:30 a.m.',
    price: '$60',
    availability: 'Full',
    state: 'complet',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1cf684cf-5dd0-4a3c-a0a5-5144e34e195a.jpg',
  },
  {
    id: 'atelier-creatif',
    name: 'Teen art studio',
    audience: 'Ages 13-17',
    place: 'Bonnyville Municipal Library',
    schedule: 'Friday · 5 p.m.',
    price: '$45',
    availability: '5 spots available',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a2858b27-8254-4ffc-98f5-bf2103bc4d3d.jpg',
  },
]

export const readiness = {
  activity: 'Drop-in pickleball',
  opensOn: 'Tuesday at 9 a.m.',
  blocking: {
    label: 'Emma’s address needs to be confirmed',
    detail: 'ATNA needs this confirmation to verify her resident eligibility.',
  },
  checks: [
    { id: 'carte', label: 'Digital card', ok: true },
    { id: 'residence', label: 'Residency status', ok: true },
    { id: 'participant', label: 'Participant profile', ok: false },
    { id: 'paiement', label: 'Payment method', ok: true },
  ],
}

export const participants = [
  { id: 'bryon', name: 'Bryon', initials: 'BM', detail: 'Cardholder' },
  { id: 'charlotte', name: 'Charlotte', initials: 'CM', detail: 'Spouse' },
  { id: 'emma', name: 'Emma', initials: 'EM', detail: '10 years old' },
]

export const registrations: Registration[] = [
  {
    id: 'r-natation',
    activity: 'Swim lessons — Junior',
    participant: 'Emma',
    initials: 'EM',
    schedule: 'Saturday · 10 a.m.',
    place: 'Bonnyville Swimming Pool',
    nextSession: 'Next session: Saturday, September 12',
  },
  {
    id: 'r-yoga',
    activity: 'Morning yoga',
    participant: 'Bryon',
    initials: 'BM',
    schedule: 'Wednesday · 7:30 a.m.',
    place: 'Bonnyville Seniors Drop-In Centre',
    nextSession: 'Next session: Wednesday, September 16',
  },
]

export const followUps = [
  'Emma’s swim lesson moves to the small pool this Saturday.',
  'Registration for the next level opens Monday — Emma will be eligible.',
]

export const facilities: Facility[] = [
  {
    id: 'salle-communautaire',
    name: 'Community hall',
    capacity: '20 to 40 people',
    availability: 'Saturday 1-5 p.m.',
    price: '$75 / 4 h',
    place: 'Bonnyville Seniors Drop-In Centre',
  },
  {
    id: 'gymnase',
    name: 'Gymnasium',
    capacity: 'Team sports',
    availability: 'Saturday 9 a.m. to noon',
    price: '$45 / h',
    place: 'Centennial Centre gymnasium',
  },
  {
    id: 'patinoire',
    name: 'Indoor rink',
    capacity: 'Full ice',
    availability: 'Sunday 8-9 p.m.',
    price: '$120 / h',
    place: 'Centennial Centre arena',
  },
]

export const equipment: Equipment[] = [
  { id: 'raquettes', name: 'Snowshoes', detail: '4 pairs · adult sizes', availability: 'From December 15', price: '$10 / day' },
  { id: 'patins', name: 'Skates', detail: 'Sizes 1 to 12', availability: 'Available', price: 'Free' },
  { id: 'communautaire', name: 'Community equipment', detail: 'Tables, tents, chairs', availability: 'By reservation', price: 'Depends on the item' },
]
